import { createMemo, createSignal, type Accessor } from 'solid-js';
import type { DrawingTool } from '@trimble/geospatial';

export interface CadEditingUiState {
  readonly cadEditingMode: Accessor<boolean>;
  readonly desiredDrawingTool: Accessor<DrawingTool | undefined>;
  readonly allowedDrawingTools: Accessor<readonly DrawingTool[]>;
  setDesiredDrawingTool(tool: DrawingTool | undefined): void;
  onSelectedLayerChanged(): void;
  reset(): void;
}

export function createCadEditingUiState(
  getMappedTools: Accessor<readonly DrawingTool[]>,
  isEnabled: Accessor<boolean>
): CadEditingUiState {
  // A request is valid only in the enabled period it was made in, so turning
  // the flag off and on again does not bring an old tool back.
  const disabledCount = createMemo<number>(
    (count) => (isEnabled() ? count : count + 1),
    0
  );
  const [request, setRequest] = createSignal<
    { readonly tool: DrawingTool; readonly period: number } | undefined
  >(undefined);
  const requestedDrawingTool = (): DrawingTool | undefined => {
    const current = request();
    return current?.period === disabledCount() ? current.tool : undefined;
  };
  const setRequestedDrawingTool = (tool: DrawingTool | undefined): void => {
    setRequest(
      tool === undefined ? undefined : { tool, period: disabledCount() }
    );
  };

  const desiredDrawingTool = createMemo(() =>
    isEnabled() ? requestedDrawingTool() : undefined
  );

  const setDesiredDrawingTool = (tool: DrawingTool | undefined): void => {
    if (tool === undefined) {
      setRequestedDrawingTool(undefined);
      return;
    }
    if (!isEnabled() || !getMappedTools().includes(tool)) {
      return;
    }
    setRequestedDrawingTool(tool);
  };

  const onSelectedLayerChanged = (): void => {
    const current = requestedDrawingTool();
    if (current !== undefined && !getMappedTools().includes(current)) {
      setRequestedDrawingTool(undefined);
    }
  };

  const reset = (): void => {
    setRequestedDrawingTool(undefined);
  };

  return {
    cadEditingMode: isEnabled,
    desiredDrawingTool,
    allowedDrawingTools: getMappedTools,
    setDesiredDrawingTool,
    onSelectedLayerChanged,
    reset,
  };
}
